import { useEffect, useState, type RefObject } from 'react';

/** Respects the user's OS-level "reduce motion" setting. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/** Tracks viewport width + height (throttled to animation frames). */
export function useViewport(): { width: number; height: number } {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));
  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        setSize({ width: window.innerWidth, height: window.innerHeight }),
      );
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(frame);
    };
  }, []);
  return size;
}

/** Sichtbarer Viewport (Höhe + Offset von oben). Auf iOS schrumpft und verschiebt
 *  sich `visualViewport` beim Öffnen der Tastatur — der Layout-Viewport bleibt
 *  unverändert, die Tastatur überlagert ihn nur. Die Vollbild-Pille deckt sich
 *  über beide Werte mit dem sichtbaren Bereich: Höhe = `hoehe` (endet an der
 *  Tastaturkante), Verschiebung = `offsetTop` (s. DynamicIsland). Fallback ohne
 *  visualViewport: volle Fensterhöhe, kein Offset. */
export function useSichtbaresViewport(): { hoehe: number; offsetTop: number } {
  const [viewport, setViewport] = useState(() =>
    typeof window !== 'undefined'
      ? {
          hoehe: window.visualViewport?.height ?? window.innerHeight,
          offsetTop: window.visualViewport?.offsetTop ?? 0,
        }
      : { hoehe: 800, offsetTop: 0 },
  );
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let frame = 0;
    const onChange = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setViewport({ hoehe: vv.height, offsetTop: vv.offsetTop }));
    };
    vv.addEventListener('resize', onChange);
    vv.addEventListener('scroll', onChange);
    return () => {
      vv.removeEventListener('resize', onChange);
      vv.removeEventListener('scroll', onChange);
      cancelAnimationFrame(frame);
    };
  }, []);
  return viewport;
}

const FOKUSSIERBAR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Fokusfalle für die geöffnete Pille (Handoff Pille §10): Tab/Shift+Tab bleiben im Element,
 * beim Öffnen wandert der Fokus hinein (falls dort noch nichts fokussiert ist), beim
 * Schließen zurück an das Element, das vorher fokussiert war.
 */
export function useFokusFalle(ref: RefObject<HTMLElement | null>, aktiv: boolean, schluessel = ''): void {
  useEffect(() => {
    if (!aktiv) return;
    const el = ref.current;
    if (!el) return;
    const vorher = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!el.contains(document.activeElement)) el.focus({ preventScroll: true });
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const ziele = Array.from(el.querySelectorAll<HTMLElement>(FOKUSSIERBAR)).filter(z => z.offsetParent !== null || z === document.activeElement);
      if (ziele.length === 0) { e.preventDefault(); el.focus(); return; }
      const erstes = ziele[0]!;
      const letztes = ziele[ziele.length - 1]!;
      const akt = document.activeElement;
      if (e.shiftKey && (akt === erstes || akt === el)) { e.preventDefault(); letztes.focus(); }
      else if (!e.shiftKey && akt === letztes) { e.preventDefault(); erstes.focus(); }
    };
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('keydown', taste);
      if (vorher?.isConnected && !el.contains(vorher)) vorher.focus({ preventScroll: true });
    };
  }, [aktiv, ref]);

  // Wechselt der Inhalt (Deck-Seite → Formular …), verschwindet oft das fokussierte Element –
  // dann den Fokus zurück in die Pille holen, statt ihn auf der Seite dahinter zu lassen.
  useEffect(() => {
    if (!aktiv) return;
    const el = ref.current;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
  }, [aktiv, ref, schluessel]);
}
